import React from "react";
import UploadFileButton from "components/UploadFileButton/UploadFileButton";
import { Colors, ProgressDisplay, Typography, useProgress } from "@factorearth/component-library";
import { recordSentry } from "utils/recordSentry";
import { JsonStructure, ProjectForResolver, SyncConfig } from "db/definitions";
import { saveJsonDirectlyToDb } from "db/merge";
import { Box } from "@factorearth/component-library/dist/Atoms/Box/Box";

interface Props {
	colors: Colors;
	getProjects: () => Promise<void>;
	online: boolean;
	open: boolean;
	projects: ProjectForResolver[];
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	showButton?: boolean;
}

function RestoreProjectFromJson(props: Props) {
	const { colors, getProjects, online, open, projects, setOpen, showButton } = props;
	const { progress, setProgress } = useProgress({
		initial: {
			value: 0,
			variant: "info",
			message: "",
			title: "",
			timeout: 30000,
			canClose: false,
			show: false,
		},
	});

	async function submitRestoreJson(file: File | null) {
		if (file) {
			return new Promise<void>((resolve, reject) => {
				const filereader = new FileReader();
				filereader.readAsText(file, "UTF-8");
				filereader.onload = async function (e) {
					try {
						if (typeof e.target?.result === "string") {
							const json = JSON.parse(e.target.result) as JsonStructure;
							if (json && json.version && json.projects) {
								const projectIndex = projects.findIndex((project) => project.id === json.projects[0].id);
								if (projectIndex === -1) {
									const syncConfig: SyncConfig = {
										online,
										source: "json",
										destination: online ? "remote" : "local",
									};
									try {
										setProgress({
											value: 0,
											variant: "info",
											message: "Uploading Json...",
											title: "Restoring Project",
											timeout: 5000,
											canClose: false,
											show: true,
										});
										await saveJsonDirectlyToDb(json, syncConfig);
										setProgress({
											value: 50,
											variant: "info",
											message: "Refreshing Project List",
											title: "Restoring Project",
											timeout: 5000,
											canClose: false,
											show: true,
										});
										await getProjects();
										setProgress({
											value: 100,
											variant: "success",
											message: "Project Successfully Restored",
											title: "Project Successfully Restored",
											timeout: 5000,
											canClose: true,
											show: true,
										});
										resolve();
									} catch (err) {
										recordSentry({ error: err, msg: "Error restoring project: ", showModal: true });
									}
									resolve();
								}
							}
						}
					} catch (err) {
						recordSentry({ error: err, msg: "Failed to parse file to json" });
					}
					resolve();
				};
			});
		}
	}

	return (
		<div>
			{progress.show && <ProgressDisplay colors={colors} progress={progress} setProgress={setProgress} />}
			{open && (
				<UploadFileButton accept="application/json" color={colors} onSubmit={submitRestoreJson} open={open} setOpen={setOpen} title="Restore Project" />
			)}
			{showButton && (
				<Box
					onClick={() => {
						setOpen(!open);
					}}
					colorPalette={colors}
					style={{
						maxWidth: "240px",
					}}>
					<Typography data-test="restore-a-project" content="Restore a Project" variant="Heading 4" textColor={colors.text.primary} />
				</Box>
			)}
		</div>
	);
}

export default RestoreProjectFromJson;
